'use client';

import { useLocale } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/navigation';
import { locales } from '@/i18n/routing';
import React, { useState, useTransition } from 'react';

type LocaleDisplayInfo = {
  locale: string;
  name: string;
  flagEmoji: string;
};

const localeInfo: Record<string, LocaleDisplayInfo> = {
  en: {
    locale: 'en',
    name: 'English',
    flagEmoji: '🇬🇧'
  },
  es: {
    locale: 'es',
    name: 'Español',
    flagEmoji: '🇪🇸'
  },
  de: {
    locale: 'de',
    name: 'Deutsch',
    flagEmoji: '🇩🇪'
  },
  nl: {
    locale: 'nl',
    name: 'Nederlands',
    flagEmoji: '🇳🇱'
  },
  pt: {
    locale: 'pt',
    name: 'Português',
    flagEmoji: '🇵🇹'
  },
  it: {
    locale: 'it',
    name: 'Italiano',
    flagEmoji: '🇮🇹'
  },
  fr: {
    locale: 'fr',
    name: 'Français',
    flagEmoji: '🇫🇷'
  }
};

export default function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const currentLocale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const changeLocale = (locale: string) => {
    // Close the dropdown
    setIsOpen(false);
    
    // Use transition for better UX
    startTransition(() => {
      // The locale will be stored in the NEXT_LOCALE cookie
      // Using replace instead of push to avoid adding to history
      router.replace(pathname, { locale: locale as any });
    });
  };
  
  const currentLocaleInfo = localeInfo[currentLocale] || localeInfo.en;
  
  return (
    <div className="position-relative">
      <button 
        className="btn btn-sm btn-outline-secondary py-1 px-3 d-flex align-items-center" 
        type="button"
        onClick={toggleDropdown}
        aria-haspopup="true" 
        aria-expanded={isOpen}
        disabled={isPending}
      >
        <span className="me-2">{currentLocaleInfo.flagEmoji}</span>
        <span>{currentLocaleInfo.name}</span>
        <i className="bx bx-chevron-down fs-sm ms-1"></i>
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show position-absolute mt-2" style={{ minWidth: '150px' }}>
          {Object.values(localeInfo).map((info) => (
            <button
              key={info.locale}
              className={`dropdown-item d-flex align-items-center ${info.locale === currentLocale ? 'active' : ''}`}
              onClick={() => changeLocale(info.locale)}
              disabled={isPending}
            >
              <span className="me-2">{info.flagEmoji}</span>
              <span>{info.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 